import React from 'react';
import { cn } from '../../lib/utils';

/**
 * Color selection control with two display modes:
 *
 * - **"grid"** (default): 6-column grid of circular swatches with name labels below.
 *   Best for base tones. Expects colors with `{ id, name, swatch }` shape.
 *
 * - **"compact"**: Flex-wrap row of small square color swatches (no labels).
 *   Best for accent/noise/surface colors. Expects colors with `{ id, name, hex }` shape.
 *
 * @param {Object}   props
 * @param {Array}    props.colors   — Array of color objects
 * @param {string}   props.value    — Currently selected color ID
 * @param {Function} props.onChange  — (colorId: string) => void
 * @param {string}   [props.variant] — "grid" | "compact" (default: "grid")
 * @param {number}   [props.columns] — Grid columns for "grid" variant (default: 6)
 * @param {string}   [props.label]   — Optional label displayed above
 */
export function ColorSwatchGrid({
  colors,
  value,
  onChange,
  variant = 'grid',
  columns = 6,
  label,
}) {
  if (variant === 'compact') {
    return (
      <div>
        {label && <div className="text-[11px] text-white/55 mb-1.5">{label}</div>}
        <div className="flex gap-1 flex-wrap">
          {colors.map((c) => (
            <button
              key={c.id}
              onClick={() => onChange(c.id)}
              className={cn(
                'h-6 w-6 rounded-md border transition-all flex items-center justify-center',
                value === c.id
                  ? 'border-white/40 scale-110'
                  : 'border-white/10 hover:border-white/25'
              )}
              title={c.name || c.label}
            >
              <span
                className={cn(
                  'h-3.5 w-3.5 rounded-sm',
                  (c.id === 'white' || c.id === 'none') && 'ring-1 ring-white/30',
                  c.id === 'black' && 'ring-1 ring-white/20',
                )}
                style={{ backgroundColor: c.id === 'none' ? 'transparent' : (c.hex || c.swatch) }}
              />
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Default: "grid" variant — circles with labels
  return (
    <div>
      {label && <div className="text-[11px] text-white/55 mb-1.5">{label}</div>}
      <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {colors.map((tone) => (
          <button
            key={tone.id}
            onClick={() => onChange(tone.id)}
            className={cn(
              'flex flex-col items-center gap-1 rounded-lg border py-1.5 px-1 transition-all',
              value === tone.id
                ? 'border-white/25 bg-white/10'
                : 'border-white/8 bg-white/[0.02] hover:bg-white/[0.06]'
            )}
          >
            <div
              className="w-5 h-5 rounded-full border border-white/15"
              style={{ backgroundColor: tone.swatch || tone.hex }}
            />
            <span className="text-[9px] text-white/50 leading-none">{tone.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
